import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Download, Upload, AlertTriangle, Info, Ban, Lock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoading } from "@/components/shared";
import api from "@/lib/api";
import { useReadOnly } from "./common";
import { Checklist } from "./Checklist";
import { CompBadge, StatusBadge } from "./scBadges";

const LOCKED = ["FINAL", "SIGNED", "TERMINATED", "ARCHIVED", "SUPERSEDED"];
const RISK = ["own_personnel_org", "own_supervision", "own_equipment", "own_business_risk", "independent_work_org", "may_use_own_employees", "no_integration"];

function F({ k, label, d, set, dis, type = "text", area }) {
  const C = area ? Textarea : Input;
  return (
    <div className={`space-y-1 ${area ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs text-slate-500">{label}</Label>
      <C type={type} value={d[k] ?? ""} disabled={dis} onChange={(e) => set(k, e.target.value)} data-testid={`scw-${k}`} />
    </div>
  );
}

function Pick({ k, label, d, set, dis, opts }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Select value={d[k] || ""} onValueChange={(v) => set(k, v)} disabled={dis}>
        <SelectTrigger data-testid={`scw-${k}`}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

const Grid = ({ children }) => <div className="grid gap-4 sm:grid-cols-2">{children}</div>;

function Parties({ t, ...p }) {
  const fk = ["name", "legal_form", "address", "country", "registration_number", "vat_id", "tax_number", "representative"];
  return (
    <div className="space-y-6">
      <Pick k="contract_model" label={t("sc.model")} opts={Object.entries(t("sc.models", { returnObjects: true }))} {...p} />
      {[["contractor", fk], ["sub", ["legal_name", ...fk.slice(1), "contact"]]].map(([pre, keys]) => (
        <div key={pre}><div className="mb-2 font-heading font-bold text-navy">{t(pre === "sub" ? "sc.subcontractor" : "sc.contractor")}</div>
          <Grid>{keys.map((k) => <F key={k} k={`${pre}_${k}`} label={t(`sc.f.${k === "legal_name" ? "name" : k}`)} {...p} />)}</Grid></div>
      ))}
    </div>
  );
}

function Country({ t, c, ...p }) {
  const e = c.effective;
  return (
    <div className="space-y-4">
      <Pick k="sub_establishment" label={t("sc.establishment")} opts={Object.entries(t("sc.est", { returnObjects: true }))} {...p} />
      <Grid><F k="sub_country" label={t("sc.f.country")} {...p} /><F k="sub_vat_id" label={t("sc.f.vat_id")} {...p} /><F k="sub_tax_number" label={t("sc.f.tax_number")} {...p} /><F k="sub_registration_number" label={t("sc.f.registration_number")} {...p} /></Grid>
      {e.responsible_finanzamt && <p className="text-sm text-slate-500" data-testid="scw-finanzamt">Finanzamt: <b className="text-navy">{e.responsible_finanzamt}</b></p>}
    </div>
  );
}

function Project({ t, sites, siteId, setSite, ...p }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1"><Label className="text-xs text-slate-500">{t("sc.project.site")}</Label>
        <Select value={siteId || "none"} onValueChange={(v) => setSite(v === "none" ? null : v)} disabled={p.dis}>
          <SelectTrigger data-testid="scw-site"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">—</SelectItem>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select></div>
      <Grid>{["project_name", "project_address", "project_id", "main_client"].map((k) => <F key={k} k={k} label={t(`sc.project.${k}`)} {...p} />)}</Grid>
    </div>
  );
}

function Pricing({ t, c, ...p }) {
  const pm = p.d.pricing_model;
  const fields = { LUMP_SUM: ["lump_sum_net", "payment_milestones"], HOURLY: ["hourly_rate", "currency", "expected_workers", "estimated_hours", "billing_interval"], UNIT: ["unit", "unit_price", "estimated_quantity"] }[pm] || [];
  return (
    <div className="space-y-4">
      <Pick k="pricing_model" label={t("sc.pricing")} opts={Object.entries(t("sc.pm", { returnObjects: true }))} {...p} />
      <Grid>{[...fields, "payment_terms"].map((k) => <F key={k} k={k} label={t(`sc.price.${k}`)} {...p} />)}</Grid>
      <Pick k="vat_treatment" label={t("sc.vat")} opts={Object.entries(t("sc.vatOpt", { returnObjects: true }))} {...p} />
      <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600" data-testid="scw-vat-suggestion"><b>{t("sc.vatSuggest")}:</b> {t(`sc.vatOpt.${c.vat_suggestion.suggested}`)} – {c.vat_suggestion.reason}</p>
    </div>
  );
}

function Workers({ t, all, sel, setSel, ...p }) {
  if (!all.length) return <p className="text-sm text-slate-500">{t("sc.noWorkers")}</p>;
  const posted = p.d.posted || {};
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <ul className="divide-y divide-slate-100" data-testid="scw-workers">
      {all.map((w) => (
        <li key={w.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
          <Checkbox checked={sel.includes(w.id)} disabled={p.dis} onCheckedChange={() => toggle(w.id)} data-testid={`scw-worker-${w.id}`} />
          <span className="flex-1 font-medium text-navy">{w.first_name} {w.last_name} <span className="text-xs text-slate-400">{w.worker_id} · {w.nationality || "—"}</span></span>
          {sel.includes(w.id) && <label className="flex items-center gap-2 text-xs text-slate-500"><Checkbox checked={posted[w.id] ?? (p.c.workers.find((x) => x.id === w.id)?.posted ?? false)} disabled={p.dis}
            onCheckedChange={(v) => p.set("posted", { ...posted, [w.id]: !!v })} data-testid={`scw-posted-${w.id}`} /> {t("sc.posted")}</label>}
        </li>
      ))}
    </ul>
  );
}

function Compliance({ t, c, sel, ...p }) {
  const risk = p.d.risk || {};
  return (
    <div className="space-y-5">
      <div><div className="mb-2 font-heading font-bold text-navy">{t("sc.freist")}</div>
        <Grid><F k="freistellung_valid_until" type="date" label={t("sc.validUntil")} {...p} /><F k="freistellung_finanzamt" label="Finanzamt" {...p} /></Grid></div>
      <Checklist sid={c.subcontractor_id} workerIds={sel} readOnly={p.dis} refreshKey={c.updated_at} />
      <div className="card-soft p-5" data-testid="scw-risk">
        <div className="mb-2 font-heading font-bold text-navy">{t("sc.risk")}</div>
        {RISK.map((k) => (
          <div key={k} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-50 py-2 text-sm">
            <span className="text-slate-600">{t(`sc.riskQ.${k}`)}</span>
            <div className="flex gap-1">{[true, false].map((v) => (
              <button key={String(v)} disabled={p.dis} onClick={() => p.set("risk", { ...risk, [k]: v })} data-testid={`scw-risk-${k}-${v ? "yes" : "no"}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${risk[k] === v ? (v ? "bg-emerald-600 text-white" : "bg-amber-500 text-white") : "bg-slate-100 text-slate-500"}`}>{t(v ? "sc.yes" : "sc.no")}</button>))}</div>
          </div>
        ))}
        {c.risk.review_recommended && <div className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="scw-risk-warning"><b>{t("sc.riskWarn")}</b> – {t("sc.riskText")}</div>}
      </div>
    </div>
  );
}

function Clauses({ t, ...p }) {
  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!p.d.vob_b} disabled={p.dis} onCheckedChange={(v) => p.set("vob_b", !!v)} data-testid="scw-vob" /> {t("sc.vob")}</label>
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!p.d.penalty_enabled} disabled={p.dis} onCheckedChange={(v) => p.set("penalty_enabled", !!v)} data-testid="scw-penalty" /> {t("sc.penalty")}</label>
      {p.d.penalty_enabled && <F k="penalty_text" area label={t("sc.penaltyText")} {...p} />}
      <Grid><F k="applicable_law" label={t("sc.law")} {...p} /><F k="jurisdiction" label={t("sc.jurisdiction")} {...p} /></Grid>
      <p className="text-xs text-slate-500">{t("sc.clausesPreview")}</p>
    </div>
  );
}

const LV_ICON = { CONTRACT_BLOCKER: Ban, WARNING: AlertTriangle, INFO: Info };
const LV_C = { CONTRACT_BLOCKER: "text-red-600", WARNING: "text-amber-600", INFO: "text-slate-500" };

function Review({ t, c, act, busy, nav }) {
  const fileRef = useRef(null);
  const dl = async (kind) => {
    const r = await api.get(`/subcon/contracts/${c.id}/download/${kind}`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `Nachunternehmervertrag_v${c.version}.${kind === "signed" ? "pdf" : kind}`; a.click();
  };
  const blockers = c.validation.filter((v) => v.level === "CONTRACT_BLOCKER");
  return (
    <div className="space-y-5">
      <ul className="space-y-1.5" data-testid="scw-validation">
        {c.validation.map((v, i) => { const I = LV_ICON[v.level]; return (
          <li key={i} className={`flex items-start gap-2 text-sm ${LV_C[v.level]}`} data-testid={`scw-val-${v.code}`}><I className="mt-0.5 h-4 w-4 shrink-0" /> <span><b>{t(`sc.lv.${v.level}`)}:</b> {v.message}</span></li>); })}
      </ul>
      {blockers.length > 0 && <p className="text-sm text-red-600">{t("sc.blockersHint")}</p>}
      <div className="flex flex-wrap gap-2">
        {!LOCKED.includes(c.status) && <Button onClick={() => act("generate")} disabled={busy || blockers.length > 0} data-testid="scw-generate" className="rounded-full bg-cyan text-white hover:bg-cyan-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sc.generate")}</Button>}
        {c.pdf_path && <Button variant="outline" className="rounded-full" onClick={() => dl("pdf")} data-testid="scw-dl-pdf"><Download className="mr-1 h-4 w-4" /> {t("sc.downloadPdf")}</Button>}
        {c.docx_path && <Button variant="outline" className="rounded-full" onClick={() => dl("docx")} data-testid="scw-dl-docx"><Download className="mr-1 h-4 w-4" /> {t("sc.downloadDocx")}</Button>}
        {c.status === "READY_FOR_REVIEW" && <Button onClick={() => act("finalize")} disabled={busy} data-testid="scw-finalize" className="rounded-full bg-navy text-white"><CheckCircle2 className="mr-1 h-4 w-4" /> {t("sc.finalize")}</Button>}
        {["READY_FOR_REVIEW", "FINAL"].includes(c.status) && (<>
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => e.target.files[0] && act("upload", e.target.files[0])} data-testid="scw-signed-input" />
          <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="scw-upload-signed"><Upload className="mr-1 h-4 w-4" /> {t("sc.uploadSigned")}</Button></>)}
        {c.signed_document_id && <Button variant="outline" className="rounded-full" onClick={() => dl("signed")} data-testid="scw-dl-signed"><Download className="mr-1 h-4 w-4" /> {t("sc.downloadSigned")}</Button>}
        {LOCKED.includes(c.status) && !c.superseded_by && <Button variant="outline" className="rounded-full" onClick={() => act("new-version")} data-testid="scw-new-version">{t("sc.newVersion")}</Button>}
        {c.status === "SIGNED" && <Button variant="outline" className="rounded-full" onClick={() => act("status", "TERMINATED")} data-testid="scw-terminate">{t("sc.terminate")}</Button>}
      </div>
      <div className="text-xs text-slate-500">{t("sc.template")}: {c.template_version} · {t("sc.versions")}: {c.versions.map((v) => (
        <button key={v.id} onClick={() => nav(`/app/employer/subcontractors/contracts/${v.id}`)} className={`ml-1 underline ${v.id === c.id ? "font-bold text-navy" : ""}`} data-testid={`scw-version-${v.version}`}>V{v.version} ({t(`sc.st.${v.status}`)})</button>))}</div>
      <p className="text-[11px] italic text-slate-400">{t("sc.disclaimer")}</p>
    </div>
  );
}

export default function SubContractWizard() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const readOnly = useReadOnly();
  const [c, setC] = useState(null);
  const [d, setD] = useState({});
  const [sel, setSel] = useState([]);
  const [siteId, setSiteId] = useState(null);
  const [step, setStep] = useState(0);
  const [sites, setSites] = useState([]);
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState(false);
  const apply = (x) => { setC(x); setD(x.effective); setSel(x.worker_ids || []); setSiteId(x.site_id); };
  useEffect(() => { setStep(0); api.get(`/subcon/contracts/${id}`).then((r) => apply(r.data)).catch(() => setC(false)); }, [id]);
  useEffect(() => {
    if (!c) return;
    api.get("/subcon/sites").then((r) => setSites(r.data.sites || [])).catch(() => {});
    api.get(`/subcon/companies/${c.subcontractor_id}`).then((r) => setAll(r.data.company.workers || [])).catch(() => {});
  }, [c?.subcontractor_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (c === null) return <PageLoading />;
  if (c === false) return <div className="text-sm text-slate-500">—</div>;
  const dis = readOnly || LOCKED.includes(c.status);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (dis) return c;
    const changed = Object.fromEntries(Object.entries(d).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(c.effective[k])));
    const { data } = await api.put(`/subcon/contracts/${c.id}`, { data: changed, worker_ids: sel, site_id: siteId });
    apply(data); return data;
  };
  const goto = async (n) => { try { await save(); setStep(n); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const act = async (kind, arg) => {
    setBusy(true);
    try {
      if (kind !== "new-version" && kind !== "status") await save();
      let r;
      if (kind === "upload") { const fd = new FormData(); fd.append("file", arg); r = await api.post(`/subcon/contracts/${c.id}/upload-signed`, fd); }
      else if (kind === "status") r = await api.post(`/subcon/contracts/${c.id}/status`, { status: arg });
      else r = await api.post(`/subcon/contracts/${c.id}/${kind}`);
      if (kind === "new-version") { nav(`/app/employer/subcontractors/contracts/${r.data.id}`); return; }
      apply(r.data); toast.success(kind === "generate" ? t("sc.generated") : t("sc.saved"));
    } catch (e) { const det = e.response?.data?.detail; toast.error(det?.issues ? det.issues.map((i) => i.message).join(", ") : (typeof det === "string" ? det : "Error")); }
    finally { setBusy(false); }
  };
  const p = { d, set, dis, c };
  const steps = t("sc.steps", { returnObjects: true });
  const body = [
    <Parties t={t} {...p} />, <Country t={t} {...p} />, <Project t={t} sites={sites} siteId={siteId} setSite={setSiteId} {...p} />,
    <Grid>{["trade", "work_description", "scope_of_services", "expected_quantity", "quality_specs"].map((k) => <F key={k} k={k} area={k !== "trade"} label={t(`sc.work.${k}`)} {...p} />)}</Grid>,
    <Grid>{["start_date", "end_date"].map((k) => <F key={k} k={k} type="date" label={t(`sc.period.${k}`)} {...p} />)}{["extension_rules", "operating_times"].map((k) => <F key={k} k={k} label={t(`sc.period.${k}`)} {...p} />)}</Grid>,
    <Pricing t={t} {...p} />, <Workers t={t} all={all} sel={sel} setSel={setSel} {...p} />, <Compliance t={t} sel={sel} {...p} />,
    <Clauses t={t} {...p} />, <Review t={t} c={c} act={act} busy={busy} nav={nav} />,
  ][step];
  return (
    <div data-testid="sc-wizard">
      <button onClick={() => nav("/app/employer/subcontractors/contracts")} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="scw-back-list"><ArrowLeft className="h-4 w-4" /> {t("sc.tContracts")}</button>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="font-heading text-2xl font-extrabold text-navy">{c.subcontractor_name}</h2>
        <span className="text-sm text-slate-400">V{c.version}</span><StatusBadge v={c.status} testid="scw-status" /><CompBadge v={c.compliance} testid="scw-compliance" />
      </div>
      {dis && !readOnly && <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="scw-locked"><Lock className="h-4 w-4" /> {t("sc.locked")}</div>}
      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <ol className="flex gap-1 overflow-x-auto lg:flex-col" data-testid="scw-steps">
          {steps.map((s, i) => (
            <li key={i}><button onClick={() => goto(i)} data-testid={`scw-step-${i + 1}`}
              className={`w-full whitespace-nowrap rounded-xl px-3 py-2 text-left text-sm font-medium ${i === step ? "bg-cyan/10 text-cyan-dark" : "text-slate-500 hover:bg-slate-100"}`}>{i + 1}. {s}</button></li>
          ))}
        </ol>
        <div className="card-soft p-6">
          <h3 className="mb-5 font-heading text-lg font-bold text-navy">{steps[step]}</h3>
          {body}
          <div className="mt-6 flex justify-between">
            <Button variant="outline" className="rounded-full" disabled={step === 0} onClick={() => goto(step - 1)} data-testid="scw-prev">{t("sc.back")}</Button>
            {step < 9 && <Button className="rounded-full bg-navy text-white" onClick={() => goto(step + 1)} data-testid="scw-next">{t("sc.next")}</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
